import React, { useState, useRef } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Alert, Typography, Card, Space, Divider } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { authService } from '../../services/auth.service';
import { useAuth } from '../../hooks/useAuth';
import { getUserFriendlyMessage } from '../../utils/error-handler';
import { publicSurveyService } from '../../services/publicSurvey.service';
import { GoogleSignInButton } from './GoogleSignInButton';

const { Title, Text } = Typography;

interface LoginFormValues {
  email: string;
  password: string;
}

interface LoginFormProps {
  compact?: boolean;
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ compact = false, onSuccess }) => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { refreshSession, signInWithGoogle } = useAuth();
  const [form] = Form.useForm<LoginFormValues>();
  const locationState = location.state as
    | {
        message?: string;
        participationSurveyId?: string;
        redirect?: string;
      }
    | undefined;
  const redirectParam = searchParams.get('redirect') || locationState?.redirect;
  const referralCode = searchParams.get('ref') ?? undefined;

  const handleSubmit = async (values: LoginFormValues) => {
    if (!captchaToken) {
      setApiError('Por favor, complete a verificação de segurança.');
      return;
    }

    setApiError(null);
    setLoading(true);

    try {
      await authService.login(values.email, values.password, captchaToken);
      // Aguarda a sessão ser atualizada no contexto de autenticação
      await refreshSession();

      onSuccess?.();

      if (locationState?.participationSurveyId) {
        try {
          const intent = await publicSurveyService.startParticipationIntent(
            locationState.participationSurveyId
          );

          if (intent.nextStep === 'GO_TO_RESPONSE_FORM' && intent.responseFormUrl) {
            navigate(intent.responseFormUrl);
            return;
          }

          if (intent.nextStep === 'VERIFY_ACCOUNT') {
            navigate('/confirm-account', {
              state: {
                email: values.email,
                participationSurveyId: locationState.participationSurveyId,
              },
            });
            return;
          }

          navigate('/', {
            state: {
              publicMessage:
                intent.message || 'Esta pesquisa não está mais disponível para participação.',
            },
          });
          return;
        } catch {
          navigate('/', {
            state: {
              publicMessage: 'Não foi possível iniciar sua participação nesta pesquisa.',
            },
          });
          return;
        }
      }

      navigate(redirectParam && redirectParam.startsWith('/') ? redirectParam : '/');
    } catch (err: any) {
      const status = err?.response?.status;
      const action = err?.response?.data?.action;

      if (status === 403 && action === 'CONFIRM_ACCOUNT') {
        navigate('/confirm-account', {
          state: {
            email: values.email,
            participationSurveyId: locationState?.participationSurveyId,
            redirect: redirectParam,
          },
        });
        return;
      }

      const errorMessage = getUserFriendlyMessage(err);
      setApiError(errorMessage);
      turnstileRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setApiError(null);
    setGoogleLoading(true);

    try {
      const { isNewAccount } = await signInWithGoogle(idToken, referralCode);

      onSuccess?.();

      if (isNewAccount) {
        navigate('/profile', {
          state: {
            message: 'Conta criada com Google! Complete a verificação do seu telefone para liberar todas as funcionalidades.',
          },
        });
        return;
      }

      navigate(redirectParam && redirectParam.startsWith('/') ? redirectParam : '/');
    } catch (err: unknown) {
      const httpError = err as { response?: { data?: { message?: string } } };
      setApiError(httpError.response?.data?.message || getUserFriendlyMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const formContent = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {!compact && (
        <>
          <Link to="/" style={{ display: 'flex', justifyContent: 'center' }}>
            <img src="/assets/brand/logo.png" alt="PePoll" style={{ height: 36 }} />
          </Link>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 0 }}>
            Login
          </Title>
        </>
      )}

      {locationState?.message && (
        <Alert message={locationState.message} type="success" showIcon />
      )}

      {apiError && (
        <Alert
          message="Erro ao fazer login"
          description={apiError}
          type="error"
          showIcon
          closable
          onClose={() => setApiError(null)}
        />
      )}

      <GoogleSignInButton onCredential={handleGoogleCredential} disabled={loading} loading={googleLoading} />

      <Divider plain style={{ margin: 0, color: '#595959', fontSize: 14, fontWeight: 400 }}>
        ou entre com e-mail
      </Divider>

      <Form
        form={form}
        name="login"
        onFinish={handleSubmit}
        layout="vertical"
        autoComplete="off"
        disabled={googleLoading}
      >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Por favor, insira seu email!' },
            { type: 'email', message: 'Email inválido!' },
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="seu@email.com"
            size="large"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          label="Senha"
          name="password"
          rules={[
            { required: true, message: 'Por favor, insira sua senha!' },
            { min: 8, message: 'Senha deve ter no mínimo 8 caracteres!' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="••••••••"
            size="large"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 8 }}>
          <Turnstile
            ref={turnstileRef}
            siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
            onSuccess={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken(null)}
            onError={() => setCaptchaToken(null)}
            options={{ theme: 'light' }}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            block
            disabled={!captchaToken || googleLoading}
          >
            Entrar
          </Button>
        </Form.Item>
      </Form>

      <div style={{ textAlign: 'center' }}>
        <Link to="/forgot-password">Esqueci minha senha</Link>
      </div>

      <div style={{ textAlign: 'center' }}>
        <Text type="secondary">
          Não tem conta?{' '}
          <Link
            to="/register"
            state={{
              participationSurveyId: locationState?.participationSurveyId,
            }}
          >
            Criar conta
          </Link>
        </Text>
      </div>
    </Space>
  );

  if (compact) {
    return formContent;
  }

  return (
    <div style={{ maxWidth: 400, margin: '80px auto', padding: '0 20px' }}>
      <Card>{formContent}</Card>
    </div>
  );
};